import { FC, useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { Button } from '@carbon/react';
import { Add } from '@carbon/react/icons';
import { PageHeader } from 'components/page-header';
import { TypeCurvesContent } from './type-curves-content';
import { TypeCurvesExportMenu } from './type-curves-export-menu';
import { ExportInventoryListingButton } from './export-inventory-listing-button';
import { inventoryWorkspacesColumns } from './inventory-workspaces-columns';
import { columnId } from 'components/data-table';
import { pageTitle } from 'utils/page-title';
import { CATALOG_ITEMS } from './type-curves-content-switcher';
import { CreateTypeCurveWorkspaceModal } from 'components/type-curve-workspace/create-type-curve-workspace-modal';
import { CreateInventoryWorkspaceModal } from 'components/type-curve-workspace/create-inventory-workspace-modal';
import type { TypeCurveHeadersQueryParams } from './atoms';
import routes from '~/routes';
import style from './type-curves-listing.module.scss';

const TAB_PARAM = 'tab';

// Validated against the switcher's own catalog rather than a hand-listed set.
// A tab the catalog offers but this function does not recognise falls back to 0,
// and the effect below immediately syncs state from the URL — so selecting it
// snapped straight back to Type Curves.
function tabFromParam(search: string): number {
  const val = Number(new URLSearchParams(search).get(TAB_PARAM));
  const known = CATALOG_ITEMS.some((item) => item.id === val);
  return known ? val : 0;
}

// Keyed by the content switcher's tab id. A lookup rather than a chain so a new
// view cannot silently inherit the last branch's title, which is what happened
// when the Inventory Workspaces tab was added.
const LISTING_TITLES: Record<number, string> = {
  0: 'Type Curves',
  1: 'Workspaces',
  2: 'Type Curve Scenarios',
  3: 'Inventory Workspaces',
};

const LISTING_HEADINGS: Record<number, string> = {
  0: 'Type Curves',
  1: 'Type Curve Workspaces',
  2: 'Type Curve Scenarios',
  3: 'Inventory Workspaces',
};

export const TypeCurvesListingPage: FC = () => {
  const history = useHistory();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [listTab, setListTab] = useState(() => tabFromParam(location.search));
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createInventoryModalOpen, setCreateInventoryModalOpen] =
    useState(false);
  const [tcListingState, setTcListingState] = useState<{
    queryParams: TypeCurveHeadersQueryParams | undefined;
    // undefined until the active listing's first response lands — the Export menu treats it as "loading"
    // rather than "no matches" (a premature 0).
    totalCount: number | undefined;
    // True while the active listing is fetching; the Excel item holds, with a loading label, until it lands.
    isFetching: boolean;
  }>({ queryParams: undefined, totalCount: undefined, isFetching: false });

  // Keep URL in sync with active tab
  const handleTabChange = useCallback(
    (tab: number) => {
      setListTab(tab);
      const params = new URLSearchParams(location.search);
      if (tab === 0) {
        params.delete(TAB_PARAM);
      } else {
        params.set(TAB_PARAM, String(tab));
      }
      const search = params.toString();
      history.replace({ search: search ? `?${search}` : '' });
    },
    [history, location.search]
  );

  // Sync tab if URL changes externally (browser back/forward)
  useEffect(() => {
    setListTab(tabFromParam(location.search));
  }, [location.search]);

  const handleTcListingStateChange = useCallback(
    (state: {
      queryParams: TypeCurveHeadersQueryParams;
      totalCount: number | undefined;
      isFetching: boolean;
    }) => {
      setTcListingState(state);
    },
    []
  );

  return (
    <>
      <Helmet>
        <title>{pageTitle(LISTING_TITLES[listTab] ?? LISTING_TITLES[0])}</title>
      </Helmet>
      <PageHeader
        breadcrumbs={[]}
        title={LISTING_HEADINGS[listTab] ?? LISTING_HEADINGS[0]}
        actions={
          <div className={style.headerActions}>
            {/* Type Curves tab — one "Export" dropdown offering the tabular (Excel) export of the
                filtered listing and the spatial (GIS) export of the type-curve-area polygons
                (INS-13270 / INS-13633). */}
            {listTab === 0 && (
              <TypeCurvesExportMenu
                params={tcListingState.queryParams}
                totalCount={tcListingState.totalCount}
                fetching={tcListingState.isFetching}
              />
            )}

            {/* Workspaces tab — create workspace only */}
            {listTab === 1 && (
              <Button
                kind="primary"
                size="md"
                renderIcon={Add}
                onClick={() => setCreateModalOpen(true)}
              >
                Create New Workspace
              </Button>
            )}

            {/* Inventories tab — CSV export, flattened to the zone grain the
                table shows. */}
            {listTab === 3 && (
              <ExportInventoryListingButton
                queryParams={tcListingState.queryParams}
                totalCount={tcListingState.totalCount ?? 0}
                columnVisibility={{}}
                columnOrder={inventoryWorkspacesColumns.map(columnId)}
              />
            )}

            {/* Inventory Workspaces tab — create, per the listing mock's
                "Create New Workspace" header action. TEMPORARILY HIDDEN.
                Restore by uncommenting; the modal and its state stay wired.
            {listTab === 3 && (
              <Button
                kind="primary"
                size="md"
                renderIcon={Add}
                onClick={() => setCreateInventoryModalOpen(true)}
              >
                Create New Workspace
              </Button>
            )}
            */}

            {/* Scenarios tab — add scenario only */}
            {listTab === 2 && (
              <Button
                kind="primary"
                size="md"
                renderIcon={Add}
                onClick={() => history.push(routes.newTypeCurveScenarioPath)}
              >
                Add Type Curve Scenario
              </Button>
            )}
          </div>
        }
      />
      <TypeCurvesContent
        listTab={listTab}
        onListTabChange={handleTabChange}
        onTcListingStateChange={handleTcListingStateChange}
      />
      {/* Launching from the listing has no type curve context — the spec
          launches create from the type curve page, so the picker opens empty
          until a type curve is chosen. See the note in the modal. */}
      <CreateInventoryWorkspaceModal
        open={createInventoryModalOpen}
        typeCurveHeaderDetailsId={null}
        onClose={() => setCreateInventoryModalOpen(false)}
      />
      <CreateTypeCurveWorkspaceModal
        open={createModalOpen}
        workspacesCollectionHref={null}
        onClose={() => setCreateModalOpen(false)}
        onCreated={() => setCreateModalOpen(false)}
        onCreatedTopLevel={() => {
          queryClient.invalidateQueries({ queryKey: ['ws-listing'] });
        }}
      />
    </>
  );
};
